/**
 * Telemetry state management.
 *
 * Tracks LLM call metrics, token usage, active visits,
 * and audio stream health for the telemetry dashboard.
 * Uses Zustand with immer for immutable updates.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface LlmCallRecord {
  taskId: string;
  kind: string;
  provider: string;
  modelId: string;
  latencyMs: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  status: 'completed' | 'failed';
  timestamp: number;
}

export interface AudioStreamHealth {
  streamId: string;
  visitId: string;
  state: string;
  rms: number;
  vadActive: boolean;
  lastUpdateAt: number;
}

export interface EventBusMetrics {
  channel: string;
  eventCount: number;
  lastEventAt: number;
}

export interface TelemetryState {
  /** Recent LLM call records (sliding window) */
  llmCalls: LlmCallRecord[];
  /** LLM queue depth */
  queueDepth: number;
  /** Active LLM tasks */
  activeTasks: number;
  /** Total completed tasks */
  completedTotal: number;
  /** Total failed tasks */
  failedTotal: number;
  /** Average latency (ms) */
  avgLatencyMs: number;
  /** Total tokens used across all calls */
  totalTokensUsed: number;
  /** Active visit count */
  activeVisitCount: number;
  /** Audio stream health entries */
  audioStreams: AudioStreamHealth[];
  /** Event bus channel metrics */
  eventBusMetrics: EventBusMetrics[];
  /** Token usage over time (bucketed by minute) */
  tokenUsageTimeline: Array<{ timestamp: number; tokens: number }>;
  /** Latency over time (bucketed by minute) */
  latencyTimeline: Array<{ timestamp: number; avgMs: number; count: number }>;
}

interface TelemetryActions {
  addLlmCall: (record: LlmCallRecord) => void;
  setQueueDepth: (depth: number) => void;
  setActiveTasks: (count: number) => void;
  setActiveVisitCount: (count: number) => void;
  updateAudioStreamHealth: (stream: AudioStreamHealth) => void;
  removeAudioStream: (streamId: string) => void;
  updateEventBusMetric: (channel: string) => void;
  clear: () => void;
}

type TelemetryStore = TelemetryState & TelemetryActions;

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MAX_LLM_CALLS = 200;
const MAX_TIMELINE_POINTS = 60; // 1 hour at 1-min intervals

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

const initialState: TelemetryState = {
  llmCalls: [],
  queueDepth: 0,
  activeTasks: 0,
  completedTotal: 0,
  failedTotal: 0,
  avgLatencyMs: 0,
  totalTokensUsed: 0,
  activeVisitCount: 0,
  audioStreams: [],
  eventBusMetrics: [],
  tokenUsageTimeline: [],
  latencyTimeline: [],
};

export const useTelemetryStore = create<TelemetryStore>()(
  immer((set) => ({
    ...initialState,

    addLlmCall: (record) => {
      set((state) => {
        state.llmCalls.push(record);
        if (state.llmCalls.length > MAX_LLM_CALLS) {
          state.llmCalls = state.llmCalls.slice(-MAX_LLM_CALLS);
        }

        // Update aggregate metrics
        if (record.status === 'completed') {
          state.completedTotal += 1;
        } else {
          state.failedTotal += 1;
        }
        state.totalTokensUsed += record.totalTokens;

        // Recalculate average latency from recent calls
        const completedCalls = state.llmCalls.filter((c) => c.status === 'completed');
        if (completedCalls.length > 0) {
          state.avgLatencyMs = Math.round(
            completedCalls.reduce((sum, c) => sum + c.latencyMs, 0) / completedCalls.length,
          );
        }

        // Update token timeline (bucket by minute)
        const minuteBucket = Math.floor(record.timestamp / 60_000) * 60_000;
        const existingBucket = state.tokenUsageTimeline.find(
          (b) => b.timestamp === minuteBucket,
        );
        if (existingBucket) {
          existingBucket.tokens += record.totalTokens;
        } else {
          state.tokenUsageTimeline.push({
            timestamp: minuteBucket,
            tokens: record.totalTokens,
          });
          if (state.tokenUsageTimeline.length > MAX_TIMELINE_POINTS) {
            state.tokenUsageTimeline = state.tokenUsageTimeline.slice(-MAX_TIMELINE_POINTS);
          }
        }

        // Update latency timeline
        const latBucket = state.latencyTimeline.find(
          (b) => b.timestamp === minuteBucket,
        );
        if (latBucket) {
          const newCount = latBucket.count + 1;
          latBucket.avgMs = Math.round(
            (latBucket.avgMs * latBucket.count + record.latencyMs) / newCount,
          );
          latBucket.count = newCount;
        } else {
          state.latencyTimeline.push({
            timestamp: minuteBucket,
            avgMs: record.latencyMs,
            count: 1,
          });
          if (state.latencyTimeline.length > MAX_TIMELINE_POINTS) {
            state.latencyTimeline = state.latencyTimeline.slice(-MAX_TIMELINE_POINTS);
          }
        }
      });
    },

    setQueueDepth: (depth) => {
      set((state) => {
        state.queueDepth = depth;
      });
    },

    setActiveTasks: (count) => {
      set((state) => {
        state.activeTasks = count;
      });
    },

    setActiveVisitCount: (count) => {
      set((state) => {
        state.activeVisitCount = count;
      });
    },

    updateAudioStreamHealth: (stream) => {
      set((state) => {
        const idx = state.audioStreams.findIndex((s) => s.streamId === stream.streamId);
        if (idx >= 0) {
          state.audioStreams[idx] = stream;
        } else {
          state.audioStreams.push(stream);
        }
      });
    },

    removeAudioStream: (streamId) => {
      set((state) => {
        state.audioStreams = state.audioStreams.filter((s) => s.streamId !== streamId);
      });
    },

    updateEventBusMetric: (channel) => {
      set((state) => {
        const existing = state.eventBusMetrics.find((m) => m.channel === channel);
        if (existing) {
          existing.eventCount += 1;
          existing.lastEventAt = Date.now();
        } else {
          state.eventBusMetrics.push({
            channel,
            eventCount: 1,
            lastEventAt: Date.now(),
          });
        }
      });
    },

    clear: () => {
      set(() => ({ ...initialState }));
    },
  })),
);
